import { Component, ChangeDetectionStrategy, Input, OnInit } from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { safeJSONParse } from '../../../common/utils/safe-json-parser';
import { formatTextFrameGroupLabel } from '../../../common/utils/text-frame-group-label';
import { CustomizableAssetConfig, PaidOptionAssetConfig, TextAssetConfig, TextFrameAssetConfig, ProductCustomizationOption } from '../../../common/interfaces';
import { NGXLogger } from 'ngx-logger';

export interface CustomizableAsset {
  type: 'image' | 'text' | 'preview-container' | 'paid-option' | string;
  text: string;
  name?: string;
  config?: CustomizableAssetConfig | null;
  amount?: number;
  productCustomizationOption?: ProductCustomizationOption | null;
}

export interface DisplayItem {
  kind: 'image' | 'message' | 'paid-option';
  label: string;
  value: string;
  required: boolean;
  amount: number;
}

export interface TextFrameOption {
  name: string;
  content: string;
}

/**
 * A single ordered render row. Preserves the customizable-asset array order
 * (which reflects the configured customization-option order) so standalone
 * text-overlay groups render at their configured position among messages/paid
 * options instead of always trailing. A `group` row is placed at the position
 * of its FIRST text-frame asset; later frames of the same group fold into it.
 */
export interface DisplayRow {
  kind: 'item' | 'group';
  item?: DisplayItem;
  group?: TextFrameGroupDisplay;
}

export interface TextFrameGroupDisplay {
  groupName: string;
  /**
   * Owning ImageFrame name for nested overlays. Undefined for standalone
   * overlays from a `custom-text-frames` option. Used by the template via
   * `formatTextFrameGroupLabel` to surface frame context inline.
   */
  imageFrameName?: string;
  /** Pre-formatted display label: `groupName` standalone, `groupName (frameName)` nested. */
  displayLabel: string;
  fontFamily: string;
  color: string;
  colorName: string;
  frames: TextFrameOption[];
}

@Component({
  selector: 'vsf-cart-line-customization',
  templateUrl: './cart-line-customization.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({ height: '0px', overflow: 'hidden', opacity: 0 })
      ),
      state(
        'expanded',
        style({ height: '*', overflow: 'auto', opacity: 1 })
      ),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
  ],
})
export class CartLineCustomizationComponent implements OnInit {
  @Input() assets: CustomizableAsset[] = [];
  @Input() optionName: string | null = null;
  @Input() folded = true;
  @Input() showHeader = true;

  displayItems: DisplayItem[] = [];
  textFrameGroups: TextFrameGroupDisplay[] = [];
  /** Single ordered render list used by the template (see DisplayRow). */
  rows: DisplayRow[] = [];
  totalAddOn = 0;

  constructor(private logger: NGXLogger) {}

  private static readonly PRINT_TEMPLATE_TEXT_ASSET_NAMES = new Set([
    'background-image',
    'customization-notes',
    'predefined-options',
  ]);

  ngOnInit(): void {
    if (!this.assets?.length) return;

    // Composite-keyed map for folding a group's frames together, shared across
    // the single ordered pass so a group row lands at its first occurrence.
    const groupMap = new Map<string, TextFrameGroupDisplay>();

    for (const asset of this.assets) {
      if (asset.type === 'preview-container') continue;

      if (asset.type === 'text-frame') {
        this.addTextFrameAsset(asset, groupMap);
        continue;
      }

      let item: DisplayItem | null = null;

      switch (asset.type) {
        case 'image':
          item = { kind: 'image', label: asset.text ?? '', value: asset.name ?? '', required: false, amount: 0 };
          break;
        case 'text':
          item = this.parseTextAsset(asset);
          break;
        case 'paid-option':
          item = this.parsePaidAsset(asset);
          break;
      }

      if (item) {
        this.displayItems.push(item);
        this.rows.push({ kind: 'item', item });
        if (item.kind === 'paid-option') {
          this.totalAddOn += item.amount;
        }
      }
    }
  }

  toggleFold() {
    if (!this.showHeader) return;
    this.folded = !this.folded;
  }

  private parseTextAsset(asset: CustomizableAsset): DisplayItem | null {
    const isPrintTemplate = CartLineCustomizationComponent.PRINT_TEMPLATE_TEXT_ASSET_NAMES.has(asset.name ?? '');

    if (isPrintTemplate) {
      const config: TextAssetConfig | null =
        typeof asset.config === 'string'
          ? safeJSONParse<TextAssetConfig>(asset.config, this.logger)
          : (asset.config as TextAssetConfig) ?? null;

      if (!config?.label || config.content === undefined) return null;

      const defaultRequired = asset.name === 'background-image' ? true : undefined;
      const required = defaultRequired ?? config.required;
      if (defaultRequired === undefined && typeof config.required !== 'boolean') return null;

      return { kind: 'message', label: config.label, value: config.content ?? '', required: !!required, amount: 0 };
    }

    const value = asset.text?.trim();
    if (!value) return null;

    const config: TextAssetConfig | null =
      typeof asset.config === 'string'
        ? safeJSONParse<TextAssetConfig>(asset.config, this.logger)
        : (asset.config as TextAssetConfig) ?? null;

    return { kind: 'message', label: config?.label || asset.name || '', value, required: !!config?.required, amount: 0 };
  }

  private parsePaidAsset(asset: CustomizableAsset): DisplayItem | null {
    const cfg: PaidOptionAssetConfig | null =
      typeof asset.config === 'string'
        ? safeJSONParse<PaidOptionAssetConfig>(asset.config, this.logger)
        : (asset.config as PaidOptionAssetConfig) ?? null;

    const name = asset.name && !CartLineCustomizationComponent.PRINT_TEMPLATE_TEXT_ASSET_NAMES.has(asset.name)
      ? asset.name
      : cfg?.label;
    if (!name) return null;

    const value = cfg?.content ?? cfg?.label ?? '';
    return { kind: 'paid-option', label: name, value, required: !!cfg?.required, amount: asset?.amount ?? 0 };
  }

  /**
   * Fold a single text-frame asset into its group. On first sight of a group
   * (composite key), append it to both `textFrameGroups` (back-compat) and the
   * ordered `rows` list at this position; subsequent frames of the same group
   * only add to its `frames`.
   */
  private addTextFrameAsset(asset: CustomizableAsset, groupMap: Map<string, TextFrameGroupDisplay>): void {
    const cfg: TextFrameAssetConfig | null =
      typeof asset.config === 'string'
        ? safeJSONParse<TextFrameAssetConfig>(asset.config, this.logger)
        : asset.config as TextFrameAssetConfig ?? null;
    if (!cfg) return;
    const optionId = asset.productCustomizationOption?.id ?? '';
    // Composite identity must include imageFrameName so same-named groups
    // nested under different image-frames don't collapse into one bucket.
    const frameName = cfg.imageFrameName ?? '';
    const key = `${optionId}::${frameName}::${cfg.groupName ?? ''}::${cfg.fontFamily ?? ''}::${cfg.color ?? ''}`;
    let group = groupMap.get(key);
    if (!group) {
      const groupName = cfg.groupName ?? asset.name ?? '';
      group = {
        groupName,
        imageFrameName: cfg.imageFrameName,
        // Surface frame context inline; flat list, no nesting/indent.
        displayLabel: formatTextFrameGroupLabel(groupName, cfg.imageFrameName),
        fontFamily: cfg.fontFamily,
        color: cfg.color,
        colorName: cfg.colorName || cfg.color,
        frames: [],
      };
      groupMap.set(key, group);
      this.textFrameGroups.push(group);
      this.rows.push({ kind: 'group', group });
    }
    group.frames.push({
      name: asset.name ?? cfg.content,
      content: cfg.content,
    });
  }
}
